import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Heart, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const ServicesPage = () => {
  const services = [
    {
      title: 'ASD Assessment',
      icon: <Brain className="w-12 h-12" />,
      description: 'Comprehensive autism spectrum assessments for all ages and genders, delivered through secure telehealth.',
      features: [
        'Women ASD assessments',
        'Men ASD assessments',
        'Child ASD assessments',
        'Fully online process',
        'Affordable pricing options'
      ],
      link: '/asd-assessment',
      color: 'var(--blue-primary)',
      image: '/images/asd-puzzle.png'
    },
    {
      title: 'ADHD Assessment',
      icon: <Users className="w-12 h-12" />,
      description: 'Professional ADHD assessments using evidence-based tools and comprehensive clinical interviews.',
      features: [
        'Adult ADHD assessments',
        'Child ADHD assessments',
        'Telehealth convenience',
        'Detailed reports',
        'Ongoing support available'
      ],
      link: '/adhd-assessment',
      color: 'var(--blue-primary)',
      image: '/images/adhd-runner.png'
    },
    {
      title: 'Therapy Services',
      icon: <Heart className="w-12 h-12" />,
      description: 'Evidence-based telehealth therapy for individuals and families across a range of mental health concerns.',
      features: [
        'Anxiety and depression',
        'Trauma and PTSD',
        'Relationship issues',
        'Life transitions',
        'Parent support'
      ],
      link: '/therapy',
      color: 'var(--blue-primary)',
      image: 'https://images.unsplash.com/photo-1588873281272-14886ba1f737?auto=format&fit=crop&w=600&q=80'
    }
  ];

  return (
    <>
      <SEO
        title="Our Services - ASD Assessment, ADHD Assessment & Therapy"
        description="Comprehensive telehealth psychology services including ASD assessments, ADHD assessments, and evidence-based therapy for all ages. Serving clients across Australia."
        keywords="psychology services, ASD assessment, ADHD assessment, online therapy, telehealth psychology, autism assessment, ADHD diagnosis"
        url="/services"
      />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Our <span style={{ color: 'var(--blue-primary)' }}>Services</span>
              </h1>
              <p className="text-lg md:text-xl" style={{ color: 'var(--text-secondary)' }}>
                Professional telehealth psychology services tailored to your needs
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {services.map((service, index) => (
              <FadeInUp key={service.title} delay={index * 0.1}>
                <div className={`bg-white rounded-3xl shadow-lg overflow-hidden ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className="lg:flex">
                    {/* Content */}
                    <div className="lg:w-2/3 p-8 md:p-12">
                      <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                        {service.title}
                      </h2>
                      <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                        {service.description}
                      </p>
                      <ul className="space-y-3 mb-8">
                        {service.features.map((feature) => (
                          <li key={feature} className="flex items-start">
                            <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: service.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={service.link}
                        data-testid={`service-cta-${service.title.toLowerCase().replace(' ', '-')}`}
                        className="inline-flex items-center px-8 py-4 rounded-full font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                        style={{ backgroundColor: service.color, color: 'white' }}
                      >
                        Learn More
                        <ArrowRight className="ml-2" size={20} />
                      </Link>
                    </div>

                    {/* Image Section */}
                    <div className="lg:w-1/3 relative">
                      <div className="h-full min-h-[300px] relative overflow-hidden">
                        <img 
                          src={service.image} 
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* Why Telehealth Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Why Choose Telehealth?
                </h2>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  All our services are delivered online for your convenience and comfort
                </p>
              </div>
            </FadeInUp>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: 'Access from Anywhere', description: 'Available across all of Australia—no travel required' },
                { title: 'Comfortable Environment', description: 'Receive support in the privacy of your own space' },
                { title: 'Flexible Scheduling', description: 'Book appointments that fit your lifestyle' },
                { title: 'Same Quality Care', description: 'Professional standards maintained in every session' }
              ].map((benefit, index) => (
                <FadeInUp key={benefit.title} delay={index * 0.1}>
                  <div className="bg-white p-6 rounded-xl">
                    <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {benefit.title}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {benefit.description}
                    </p>
                  </div>
                </FadeInUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="bg-[var(--blue-primary)] text-white rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Begin?
              </h2>
              <p className="text-lg mb-8 opacity-90">
                Book your assessment or therapy session today
              </p>
              <Link
                to="/contact"
                data-testid="services-cta-button"
                className="px-8 py-4 bg-white text-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300 hover:scale-105 inline-block"
              >
                Get Started
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;